import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Container,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Lock as LockIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import axios from "axios";
import {
  getAssessorsForAssessment,
  updateAssessorStatus,
  submitAssessorAcceptanceToNABL,
  getAssessorProfile,
} from "../../services/api";
import { decryptText, encryptText } from "../../utils/encryption";
import { toast } from "react-toastify";

const AcceptAssessors = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getParamsFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    const encryptedOrgId = searchParams.get("orgId");
    const encryptedP = searchParams.get("p");
    
    // Helper function to try different decryption methods
    const tryDecrypt = (encryptedValue) => {
      if (!encryptedValue) return null;
      
      // First try AES decryption (new method)
      try {
        const decrypted = decryptText(encryptedValue);
        if (decrypted && decrypted.trim() !== '') {
          return decrypted;
        }
      } catch (error) {
        console.log('AES decryption failed, trying Base64');
      }
      
      // Fallback to Base64 decryption (old method)
      try {
        const decoded = atob(encryptedValue);
        if (decoded && decoded.trim() !== '') {
          console.log('✅ Used Base64 decryption');
          return decoded;
        }
      } catch (error) {
        console.error('Base64 decryption also failed:', error);
      }
      
      return null;
    };
    
    const decryptedAppId = tryDecrypt(encryptedAppId);
    const decryptedOrgId = tryDecrypt(encryptedOrgId);
    const decryptedP = tryDecrypt(encryptedP);
    
    console.log('🔓 Decrypted - appId:', decryptedAppId);
    console.log('🔓 Decrypted - orgId:', decryptedOrgId);
    console.log('🔓 Decrypted - p:', decryptedP);
    
    return {
      appId: decryptedAppId,
      orgId: decryptedOrgId,
      skipPassword: decryptedP === 'true',
    };
  };
 
  const { appId, orgId, skipPassword } = getParamsFromQuery();
  const fetchAssessorsData = async (appId) => {
     try {
       setLoading(true);
       const response = await getAssessorsForAssessment(appId);
 
       if (response.data.success) {
         setAssessors(response.data.data);
 
         // Initialize selected actions and remarks from existing data
         const actions = {};
         const remarks = {};
 
         response.data.data.forEach(assessor => {
           if (assessor.CabAccepted === 1) {
             actions[assessor.AssessorAllotedId] = 'accept';
           } else if (assessor.CabAccepted === 2) {
             actions[assessor.AssessorAllotedId] = 'reject';
             remarks[assessor.AssessorAllotedId] = assessor.CABDenyReason || '';
           }
         });
 
         setSelectedActions(actions);
         setRejectRemarks(remarks);
 
         // Check if all assessors have been accepted/rejected
         checkSubmitButtonVisibility(response.data.data);
         showSnackbar("");
       } else {
         showSnackbar(response.data.message || "Failed to fetch assessors data", "error");
       }
     } catch (error) {
       console.error("Error fetching assessors:", error);
       showSnackbar("Failed to connect to server", "error");
     } finally {
       setLoading(false);
     }
   };

  const [passwordVerified, setPasswordVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [assessors, setAssessors] = useState([]);
  const [labDetails, setLabDetails] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedActions, setSelectedActions] = useState({});
  const [rejectRemarks, setRejectRemarks] = useState({});
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [sendPassword, setSendPassword] = useState('');
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [labTypeId, setLabTypeId] = useState(null);

  // Show snackbar message
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const checkSubmitButtonVisibility = useCallback((assessorsList) => {
    // Check if all assessors have been accepted or rejected
    const allActioned = assessorsList.every(
      assessor => assessor.CabAccepted === 1 || assessor.CabAccepted === 2
    );
    
    // Check if at least one is accepted
    const hasAccepted = assessorsList.some(
      assessor => assessor.CabAccepted === 1
    );
      const hasReject = assessorsList.some(
      assessor => assessor.CabAccepted === 2
    );
    
    setShowSubmitButton(allActioned && (hasAccepted || hasReject));
  }, []);


  // Fetch assessors and lab details
  const fetchData = useCallback(async () => {
    try {
      // Fetch lab details
      const labResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/Getlabdetails?appid=${appId}&orgid=${orgId}`
      );

      if (labResponse.data.isstatus) {
        setLabDetails(labResponse.data);
        setLabTypeId(labResponse.data.LabTypeId || null);
      }

      // Fetch assessors
      const assessorResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/Getassessor?appid=${appId}&orgid=${orgId}&disciplineId=`
      );

      if (assessorResponse.data.isstatus) {
        const assessorData = assessorResponse.data.assessor || [];
        setAssessors(assessorData);
        
        // Initialize selected actions and remarks from existing data
        const actions = {};
        const remarks = {};

        assessorData.forEach(assessor => {
          if (assessor.CabAccepted === 1) {
            actions[assessor.id] = 'accept';
          } else if (assessor.CabAccepted === 2) {
            actions[assessor.id] = 'reject';
            remarks[assessor.id] = assessor.CABDenyReason || '';
          }
        });

        setSelectedActions(actions);
        setRejectRemarks(remarks);

        // Check if all assessors have been accepted/rejected
        checkSubmitButtonVisibility(assessorData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("Unable to fetch data. Please try again.", "error");
    }
  }, [appId, orgId, checkSubmitButtonVisibility, showSnackbar]);
  // Check if password should be skipped on component mount
  useEffect(() => {
    const checkPasswordBypass = async () => {
      if (skipPassword && appId && orgId) {
        console.log('✅ Password bypass enabled via p parameter');
        setPasswordVerified(true);
        await fetchData();
      }
    };

    checkPasswordBypass();
  }, [skipPassword, appId, orgId, fetchData]);

  // Verify password
  const handleVerifyPassword = async () => {
    if (!password) {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: orgId,
          password: password,
          appId: appId,
        }
      );

      if (response.data.success) {
        setPasswordVerified(true);
        showSnackbar("Password verified successfully", "success");
        // Fetch assessors and lab details
        await fetchData();
      } else {
        showSnackbar(response.data.message || "Invalid password", "error");
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      
      // Check if it's a stage validation error
      if (error.response?.status === 403 && error.response?.data?.alreadySubmitted) {
        showSnackbar(error.response.data.message || "Your response has been submitted. This link is no longer active.", "error");
      } else {
        showSnackbar(error.response?.data?.message || "Unable to verify password. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActionChange = (assessorId, action) => {
    setSelectedActions(prev => ({
      ...prev,
      [assessorId]: action
    }));

    // Clear reject remark if switching to accept
    if (action === 'accept') {
      setRejectRemarks(prev => {
        const updated = { ...prev };
        delete updated[assessorId];
        return updated;
      });
    }
  };

  const handleRejectRemarkChange = (assessorId, value) => {
    setRejectRemarks(prev => ({
      ...prev,
      [assessorId]: value
    }));
  };

   const handleSubmitAction = async (id, assessorAllotedId) => {
      const action = selectedActions[id];
  
      if (!action) {
        showSnackbar('Please select an action (Accept/Reject)', 'error');
        return;
      }
  
      if (action === 'reject' && !rejectRemarks[id]?.trim()) {
        showSnackbar('Please enter reject remark', 'error');
        return;
      }
  
      try {
        setLoading(true);
  
        const response = await updateAssessorStatus({
          id,
          action,
          assessorAllotedId,
          rejectRemark: rejectRemarks[id] || '',
          appId: appId,
          assessmentType: 'Main'
        });
  
        if (response.data.success) {
          showSnackbar(response.data.message || 'Assessor status updated successfully', 'success');
            // Fetch assessors
      const assessorResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor/Getassessor?appid=${appId}&orgid=${orgId}&disciplineId=`
      );

      if (assessorResponse.data.isstatus) {
        const assessorData = assessorResponse.data.assessor || [];
        setAssessors(assessorData);
        
        // Initialize selected actions and remarks from existing data
        const actions = {};
        const remarks = {};

        assessorData.forEach(assessor => {
          if (assessor.CabAccepted === 1) {
            actions[assessor.id] = 'accept';
          } else if (assessor.CabAccepted === 2) {
            actions[assessor.id] = 'reject';
            remarks[assessor.id] = assessor.CABDenyReason || '';
          }
        });

        setSelectedActions(actions);
        setRejectRemarks(remarks);

        // Check if all assessors have been accepted/rejected
        checkSubmitButtonVisibility(assessorData);
      }
        } else {
          showSnackbar(response.data.message || 'Failed to update assessor status', 'error');
        }
      } catch (error) {
        console.error('Error updating assessor status:', error);
        toast.error('Failed to update assessor status');
      } finally {
        setLoading(false);
      }
    };

  const handleViewProfile = (assessorId) => {
    window.open(
      `/main/assessor-profile?AssessorId=${encryptText(assessorId)}&type=${encryptText('Lab')}`,
      "_blank"
    );
  };

  const handleSendToNABL = () => {
    setPasswordDialog(true);
  };

  const handlePasswordSubmit = async () => {
    if (!sendPassword.trim()) {
      showSnackbar('Please enter your password', 'error');
      return;
    }

    try {
      setLoading(true);

      // Verify password first and check stage
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: orgId,
          password: sendPassword,
          appId: appId,
        }
      );

      if (!verifyResponse.data.success) {
        showSnackbar('Invalid password', 'error');
        setLoading(false);
        return;
      }

      // Check if any assessor is rejected by CAB
      const anyRejected = assessors.some(assessor => assessor.CabAccepted === 2);
      const newStage = anyRejected ? '19d' : '19c';

      // Submit to NABL with the determined stage
      const response = await submitAssessorAcceptanceToNABL({
        appId,
        orgId,
        password: sendPassword,
        assessmentType: 'Main'
      });

      if (response.data.success) {
        const stageMessage = anyRejected 
          ? 'Some assessors were rejected. Notification sent to dealing officer.'
          : 'All assessors accepted.';
        
        showSnackbar(`${stageMessage} Sent to NABL successfully!`, 'success');
        setPasswordDialog(false);
        setSendPassword('');
        
        setTimeout(() => {
          navigate('/main/dashboard');
        }, 2000);
      } else {
        showSnackbar(response.data.message || 'Failed to send to NABL', 'error');
      }
    } catch (error) {
      console.error('Error submitting to NABL:', error);
      
      // Check if it's a stage validation error
      if (error.response?.status === 403 && error.response?.data?.alreadySubmitted) {
        showSnackbar(error.response.data.message || 'Your response has been submitted. This link is no longer active.', 'error');
        setPasswordDialog(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        showSnackbar(error.response?.data?.message || 'Failed to send to NABL', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getActionStatus = (assessor) => {
    if (assessor.CabAccepted === 1) {
      return { label: 'Accepted', color: 'success' };
    } else if (assessor.CabAccepted === 2) {
      return { label: 'Rejected', color: 'error' };
    }
    return { label: 'Pending', color: 'warning' };
  };

  const getCapacityName = (assessor) => {
    if (assessor.Capicity){
    if (assessor.Capicity === "IsLA") return "LA";
    if (assessor.Capicity === "LA") return "LA";
    if (assessor.Capicity === "IsTL") return "TL";
    if (assessor.Capicity === "LA_TA") return "LA";
    if (assessor.Capicity === "TE") return "TE";
    if (assessor.Capicity === "TA") return "TA";
    if (assessor.Capicity === "Observer") return "Observer";
    } else {
      return assessor.Capicity || "N/A";
    }

    
    return "N/A";
  };
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Password verification dialog
  if (!passwordVerified) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: "90%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 3,
              color: "white",
              textAlign: "center",
            }}
          >
            <LockIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Verify Your Password
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              Please enter your password to access the assessor acceptance page.
            </Typography>

            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleVerifyPassword();
                }
              }}
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyPassword}
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  background: "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Password"}
            </Button>
          </CardContent>
        </Card>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Main content after password verification
  return (
    <Box sx={{ minHeight: "100vh", py: 4, backgroundColor: "#f5f5f5", mt: 8 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              borderColor: "#666",
              color: "#666",
              "&:hover": { borderColor: "#333", color: "#333" },
            }}
          >
            Back
          </Button> */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
            Assessment Assessors
          </Typography>
        </Box>

        {/* Lab Information Card */}
        <Card elevation={1} sx={{ mb: 4, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <BusinessIcon sx={{ color: "#666", mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Laboratory Information
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#e3f2fd",
                    borderRadius: 2,
                    border: "1px solid #90caf9",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#1565c0", mb: 0.5, fontWeight: 500 }}
                  >
                    Laboratory Name:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#0d47a1" }}
                  >
                    {labDetails.labName || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Lab Type:
                </Typography>
                <Chip
                  label={labDetails.LabType || 'N/A'}
                  size="medium"
                  sx={{
                    bgcolor: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #e0e0e0",
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Lab ID:
                </Typography>
                <Chip
                  label={labDetails.Labid || 'N/A'}
                  size="medium"
                  color="warning"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Assessors Table */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{ p: 3, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Selected Assessors
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Sr. No.</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>
                    {labTypeId === 1 || labTypeId === 2 ? "Discipline/Group" : "Discipline"}
                  </TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Assessor</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Capacity</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>From Date</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>To Date</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Profile</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ color: "#333", fontWeight: 600 }}>Submit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={labTypeId === 3 ? 9 : 10} align="center">
                      <Typography variant="body2" sx={{ color: "#666", py: 3 }}>
                        No assessors allocated yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assessors.map((assessor, index) => {
                    const actionStatus = getActionStatus(assessor);

                    return (
                      <TableRow
                        key={assessor.id || index}
                        hover
                        sx={{ "&:hover": { bgcolor: "#fafafa" } }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {labTypeId === 1 || labTypeId === 2 ? (
                            <>
                              <div
                                style={{ color: "#1976d2", fontWeight: 500 }}
                              >
                                {assessor.DisciplineName || "N/A"}
                              </div>

                              <div
                                style={{ color: "#2e7d32", fontWeight: 600 }}
                              >
                                {assessor.GroupName || ""}
                              </div>
                            </>
                          ) : (
                            assessor.DisciplineName || "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <PersonIcon
                              sx={{ color: "#666", mr: 1, fontSize: 18 }}
                            />
                            {assessor.AssessorName || assessor.Name || "N/A"}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getCapacityName(assessor)}
                            size="small"
                            sx={{
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(assessor.datefrom || assessor.DateFrom)}
                        </TableCell>
                        <TableCell>
                          {formatDate(assessor.dateto || assessor.DateTo)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Profile">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewProfile(
                                  assessor.Assessorid || assessor.AllotedToId,
                                )
                              }
                              sx={{ color: "#1976d2" }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {assessor.CabAccepted === 1 ||
                          assessor.CabAccepted === 2 ? (
                            <Chip
                              label={actionStatus.label}
                              color={actionStatus.color}
                              variant="contained"
                            />
                          ) : (
                            <RadioGroup
                              value={selectedActions[assessor.id] || ""}
                              onChange={(e) =>
                                handleActionChange(assessor.id, e.target.value)
                              }
                            >
                              <FormControlLabel
                                value="accept"
                                control={<Radio size="small" />}
                                label="Accept"
                              />
                              <FormControlLabel
                                value="reject"
                                control={<Radio size="small" />}
                                label="Reject"
                              />
                            </RadioGroup>
                          )}
                          {selectedActions[assessor.id] === "reject" && (
                            <TextField
                              multiline
                              rows={3}
                              placeholder="Reject Remark*"
                              value={rejectRemarks[assessor.id] || ""}
                              onChange={(e) =>
                                handleRejectRemarkChange(
                                  assessor.id,
                                  e.target.value,
                                )
                              }
                              size="small"
                              fullWidth
                              sx={{ minWidth: 200, mt: 1 }}
                            />
                          )}
                          {assessor.CabAccepted === 2 && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#d32f2f", mt: 1 }}
                            >
                              Reason: {assessor.CABDenyReason || "N/A"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(assessor.CabAccepted === 0 ||
                            assessor.CabAccepted === "") &&
                          selectedActions[assessor.id] ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleSubmitAction(
                                  assessor.id,
                                  assessor.AssessorAllotedId,
                                )
                              }
                              disabled={loading}
                              sx={{
                                bgcolor: "#1976d2",
                                "&:hover": { bgcolor: "#1565c0" },
                              }}
                            >
                              Submit
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Send to NABL Button */}
        {showSubmitButton && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSendToNABL}
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                px: 5,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                  boxShadow: "0 6px 16px rgba(102,126,234,0.6)",
                },
              }}
            >
             Send to NABL
            </Button>
          </Box>
        )}
      </Container>

      {/* Password Dialog for Send to NABL */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>Enter Your Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={sendPassword}
            onChange={(e) => setSendPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handlePasswordSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AcceptAssessors;
